export interface Labelled {
  synonyms?: Record<string, string>
  synonym?: string
  title?: string
  name?: string
}

/**
 * A Designer-authored form Title is an override when it is not just the
 * generated copy of `name` / `synonym` (Item/List generators stamp
 * `title = synonym || name`). Cascade: custom Title > synonyms[locale] >
 * synonym > name. Same rule for ordinary and branch-common attributes.
 */
function hasSynonymMap(synonyms: Record<string, string> | undefined): boolean {
  return Boolean(synonyms && Object.keys(synonyms).length > 0)
}

function humanizeName(name: string): string {
  if (!name) return ''
  return name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ')
}

function isCustomFormTitle(el: Labelled): boolean {
  const title = el.title?.trim()
  if (!title) return false
  if (title === (el.name ?? '')) return false
  if (el.synonym?.trim() === title) return false
  const values = el.synonyms ? Object.values(el.synonyms) : []
  if (values.some((v) => v && v.trim() === title)) return false
  // Generators stamp title = synonym || name. For branch commons the
  // platform synonym is the humanized name ("date" → "Date"). That is not
  // a Designer Title override — TPS vs tps stays custom (humanize is "Tps").
  if (title === humanizeName(el.name ?? '')) return false
  return true
}

function isGeneratedCaption(value: string, el: Labelled): boolean {
  const v = value.trim()
  return v === (el.name ?? '') || v === humanizeName(el.name ?? '')
}

export function resolveLabel(el: Labelled, locale: string | null, defaultLocale?: string): string {
  if (isCustomFormTitle(el)) return el.title!.trim()
  if (!locale) {
    return el.synonym || el.title || el.name || ''
  }
  const synonym = el.synonym?.trim()
  const s = { ...(el.synonyms || {}) }
  const defaultKey = defaultLocale || locale
  if (synonym && defaultKey) {
    const mapped = s[defaultKey]
    if (!mapped || isGeneratedCaption(mapped, el)) {
      s[defaultKey] = synonym
    }
  }
  for (const key of [locale, defaultLocale]) {
    if (!key) continue
    const v = s[key]
    if (!v) continue
    if (synonym && isGeneratedCaption(v, el) && v !== synonym) continue
    return v
  }
  return synonym || el.title || el.name || ''
}

export interface AttributeSynonymSource {
  name: string
  synonym?: string
  synonyms?: Record<string, string>
}

/**
 * Fallback for schema nodes/columns that carry no synonym of their own:
 * borrow the matching attribute's scalar synonym + map (looked up by name,
 * via `dataPath` when present). Protects hand-built custom forms/lists —
 * the FormEditor has no per-node synonym UI, and the list/item form
 * generators never copied synonym/synonyms onto their output nodes — and
 * means "Regenerate from default" can never silently drop a translation,
 * since the caption still lives on the object's attribute config (including
 * branch commons), not on the (possibly persisted) schema node.
 */
export function mergeAttributeSynonyms<T extends Labelled & { dataPath?: string }>(
  el: T,
  attributes: AttributeSynonymSource[] | undefined | null,
): T {
  if (!attributes?.length) return el
  const key = (el.dataPath?.replace('Object.', '') || el.name || '').toLowerCase()
  if (!key) return el
  const attr = attributes.find((a) => a.name.toLowerCase() === key)
  if (!attr) return el
  const next = { ...el }
  let changed = false
  if (hasSynonymMap(attr.synonyms)) {
    const nodeMap = hasSynonymMap(el.synonyms) ? el.synonyms! : {}
    const filled = { ...attr.synonyms, ...nodeMap }
    const filledHole = Object.keys(attr.synonyms!).some((k) => !nodeMap[k] && attr.synonyms![k])
    if (!hasSynonymMap(el.synonyms) || filledHole) {
      next.synonyms = filled
      changed = true
    }
  }
  if (!el.synonym?.trim() && attr.synonym?.trim()) {
    next.synonym = attr.synonym
    changed = true
  }
  return changed ? next : el
}
